import React, { useState } from 'react';
import {
  FileText,
  ArrowLeft,
  Search,
  Filter,
  Plus,
  Edit,
  Eye,
  Download,
  Upload,
  Calendar,
  Clock,
  CheckCircle,
  AlertTriangle,
  XCircle,
  DollarSign,
  Building,
  User,
  Package,
  BarChart3,
  TrendingUp,
  Activity,
  Shield,
  Award,
  Settings
} from 'lucide-react';
import { contractData } from '../../data/purchaseCycleData';

const ContractManagement = ({ onBack, navigationSource }) => {
  const [activeView, setActiveView] = useState('dashboard');
  const [selectedContract, setSelectedContract] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const { purchaseContracts, contractDashboard } = contractData;

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'pending_approval': return 'text-yellow-600 bg-yellow-100';
      case 'expired': return 'text-red-600 bg-red-100';
      case 'draft': return 'text-gray-600 bg-gray-100';
      case 'terminated': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active': return '有效';
      case 'pending_approval': return '待核准';
      case 'expired': return '已過期';
      case 'draft': return '草稿';
      case 'terminated': return '已終止';
      default: return '未知';
    }
  };

  const getTypeText = (type) => {
    switch (type) {
      case 'framework': return '框架合約';
      case 'fixed': return '固定合約';
      case 'service': return '服務合約';
      case 'lease': return '租賃合約';
      default: return type;
    }
  };

  const getMilestoneStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      case 'overdue': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getMilestoneStatusText = (status) => {
    switch (status) {
      case 'completed': return '已完成';
      case 'in_progress': return '進行中';
      case 'pending': return '待開始';
      case 'overdue': return '逾期';
      default: return '未知';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('zh-TW');
  };

  const renderWorkflowBanner = () => {
    if (!navigationSource) return null;
    
    return (
      <div className="mb-6 bg-gradient-to-r from-orange-500 to-orange-600 text-white p-4 rounded-lg shadow-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileText className="w-6 h-6" />
            <div>
              <h2 className="text-lg font-semibold">採購週期管理 - 合約管理</h2>
              <p className="text-orange-100 text-sm">Purchase Cycle Management - Contract Management</p>
            </div>
          </div>
          <button
            onClick={onBack}
            className="flex items-center space-x-2 bg-white bg-opacity-20 hover:bg-opacity-30 px-4 py-2 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>返回採購週期</span>
          </button>
        </div>
      </div>
    );
  };

  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">總合約數</p>
              <p className="text-2xl font-bold text-gray-900">{contractDashboard.totalContracts}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <FileText className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <TrendingUp className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">有效合約 {contractDashboard.activeContracts} 個</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">合約總值</p>
              <p className="text-2xl font-bold text-gray-900">{formatCurrency(contractDashboard.totalValue)}</p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <BarChart3 className="w-4 h-4 text-blue-500 mr-1" />
            <span className="text-blue-600">平均值 {formatCurrency(contractDashboard.averageContractValue)}</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">待核准</p>
              <p className="text-2xl font-bold text-orange-600">{contractDashboard.pendingContracts}</p>
            </div>
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <AlertTriangle className="w-4 h-4 text-orange-500 mr-1" />
            <span className="text-orange-600">需要審核</span>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">績效評分</p>
              <p className="text-2xl font-bold text-gray-900">{contractDashboard.performanceMetrics.qualityCompliance}%</p>
            </div>
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
              <Award className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <div className="mt-4 flex items-center text-sm">
            <CheckCircle className="w-4 h-4 text-green-500 mr-1" />
            <span className="text-green-600">品質合規率</span>
          </div>
        </div>
      </div>

      {/* Contract Type Breakdown */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">合約類型分布</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contractDashboard.contractsByType.map((type, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{getTypeText(type.type)}</h4>
                <span className="text-sm text-gray-500">{type.count} 個</span>
              </div>
              <p className="text-2xl font-bold text-blue-600">{formatCurrency(type.value)}</p>
              <p className="text-sm text-gray-500">總合約價值</p>
            </div>
          ))}
        </div>
      </div>

      {/* Contract Status */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">合約狀態統計</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {contractDashboard.contractsByStatus.map((status, index) => (
            <div key={index} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h4 className="font-medium text-gray-900">{getStatusText(status.status)}</h4>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status.status)}`}>
                  {status.count} 個
                </span>
              </div>
              <p className="text-2xl font-bold text-green-600">{formatCurrency(status.value)}</p>
              <p className="text-sm text-gray-500">合約總值</p>
            </div>
          ))}
        </div>
      </div>

      {/* Performance Metrics */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">績效指標</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-600">{contractDashboard.performanceMetrics.onTimeDelivery}%</p>
            <p className="text-sm text-gray-500">準時交貨率</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-600">{contractDashboard.performanceMetrics.qualityCompliance}%</p>
            <p className="text-sm text-gray-500">品質合規率</p>
          </div>
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <DollarSign className="w-8 h-8 text-purple-600" />
            </div>
            <p className="text-2xl font-bold text-purple-600">{formatCurrency(contractDashboard.performanceMetrics.costSavings)}</p>
            <p className="text-sm text-gray-500">成本節省</p>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">快速操作</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <button
            onClick={() => setActiveView('contracts')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Eye className="w-5 h-5 text-blue-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">查看合約清單</p>
              <p className="text-sm text-gray-500">管理所有採購合約</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('pending')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <Clock className="w-5 h-5 text-orange-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">待核准合約</p>
              <p className="text-sm text-gray-500">處理等待核准的合約</p>
            </div>
          </button>
          
          <button
            onClick={() => setActiveView('templates')}
            className="flex items-center space-x-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <FileText className="w-5 h-5 text-green-600" />
            <div className="text-left">
              <p className="font-medium text-gray-900">合約範本</p>
              <p className="text-sm text-gray-500">管理合約範本庫</p>
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  const renderContracts = () => (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">合約清單</h2>
          <p className="text-gray-600">管理所有採購合約</p>
        </div>
        <div className="flex items-center space-x-3">
          <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
            <Plus className="w-4 h-4" />
            <span>新增合約</span>
          </button>
          <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <Upload className="w-4 h-4" />
            <span>匯入</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow-md">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="搜尋合約名稱或編號..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有狀態</option>
            <option value="active">有效</option>
            <option value="pending_approval">待核准</option>
            <option value="expired">已過期</option>
            <option value="draft">草稿</option>
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="all">所有類型</option>
            <option value="framework">框架合約</option>
            <option value="fixed">固定合約</option>
            <option value="service">服務合約</option>
            <option value="lease">租賃合約</option>
          </select>
          
          <button className="flex items-center space-x-2 bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors">
            <Download className="w-4 h-4" />
            <span>匯出</span>
          </button>
        </div>
      </div>

      {/* Contracts Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">合約資訊</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">供應商</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">合約類型</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">合約價值</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">有效期間</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">狀態</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">付款條件</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {purchaseContracts.map((contract) => (
                <tr key={contract.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{contract.contractName}</div>
                      <div className="text-sm text-gray-500">{contract.contractNumber}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contract.supplierName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {getTypeText(contract.contractType)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(contract.contractValue)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <div>
                      <div>{formatDate(contract.startDate)}</div>
                      <div className="text-gray-500">至 {formatDate(contract.endDate)}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(contract.status)}`}>
                      {getStatusText(contract.status)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {contract.paymentTerms}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedContract(contract);
                          setActiveView('contract-detail');
                        }}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button className="text-green-600 hover:text-green-900">
                        <Edit className="w-4 h-4" />
                      </button>
                      <button className="text-purple-600 hover:text-purple-900">
                        <Download className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderContractDetail = () => {
    if (!selectedContract) return null;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setActiveView('contracts')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">{selectedContract.contractName}</h2>
              <p className="text-gray-600">{selectedContract.contractNumber}</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
              <Edit className="w-4 h-4" />
              <span>編輯</span>
            </button>
            <button className="flex items-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors">
              <Download className="w-4 h-4" />
              <span>下載</span>
            </button>
          </div>
        </div>

        {/* Contract Overview */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">合約價值</h3>
            <p className="text-3xl font-bold text-blue-600">{formatCurrency(selectedContract.contractValue)}</p>
            <p className="text-sm text-gray-500 mt-2">幣別: {selectedContract.currency}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">合約期間</h3>
            <p className="text-lg font-medium text-gray-900">{formatDate(selectedContract.startDate)}</p>
            <p className="text-sm text-gray-500">至 {formatDate(selectedContract.endDate)}</p>
          </div>
          
          <div className="bg-white p-6 rounded-lg shadow-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">合約狀態</h3>
            <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(selectedContract.status)}`}>
              {getStatusText(selectedContract.status)}
            </span>
            <p className="text-sm text-gray-500 mt-2">類型: {getTypeText(selectedContract.contractType)}</p>
          </div>
        </div>

        {/* Contract Details */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">合約詳情</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">供應商</p>
                <p className="font-medium text-gray-900">{selectedContract.supplierName}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">付款條件</p>
                <p className="font-medium text-gray-900">{selectedContract.paymentTerms}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">交貨條件</p>
                <p className="font-medium text-gray-900">{selectedContract.deliveryTerms}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">品質要求</p>
                <p className="font-medium text-gray-900">{selectedContract.qualityRequirements}</p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div>
                <p className="text-sm text-gray-500">罰款條款</p>
                <p className="font-medium text-gray-900">{selectedContract.penaltyClause}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">保固期間</p>
                <p className="font-medium text-gray-900">{selectedContract.warrantyPeriod}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">建立日期</p>
                <p className="font-medium text-gray-900">{formatDate(selectedContract.createdDate)}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">最後修改</p>
                <p className="font-medium text-gray-900">{formatDate(selectedContract.lastModified)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Contract Items */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">合約項目</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">項目代碼</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">項目名稱</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">單價</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">最小數量</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">最大數量</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">前置時間</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {selectedContract.contractItems.map((item, index) => (
                  <tr key={index}>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.itemCode}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.itemName}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{formatCurrency(item.unitPrice)}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.minQuantity.toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.maxQuantity.toLocaleString()}</td>
                    <td className="px-4 py-2 text-sm text-gray-900">{item.leadTime} 天</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Milestones */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">里程碑</h3>
          <div className="space-y-4">
            {selectedContract.milestones.map((milestone) => (
              <div key={milestone.id} className="flex items-center space-x-4 p-4 border rounded-lg">
                <div className="flex-shrink-0">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getMilestoneStatusColor(milestone.status)}`}>
                    {getMilestoneStatusText(milestone.status)}
                  </span>
                </div>
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{milestone.description}</p>
                  <p className="text-sm text-gray-500">到期日: {formatDate(milestone.dueDate)}</p>
                  {milestone.completionDate && (
                    <p className="text-sm text-green-600">完成日: {formatDate(milestone.completionDate)}</p>
                  )}
                </div>
                <div className="flex-shrink-0">
                  {milestone.status === 'completed' ? (
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  ) : milestone.status === 'in_progress' ? (
                    <Activity className="w-5 h-5 text-blue-600" />
                  ) : (
                    <Clock className="w-5 h-5 text-gray-400" />
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Documents */}
        <div className="bg-white p-6 rounded-lg shadow-md">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">相關文件</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {selectedContract.documents.map((document, index) => (
              <div key={index} className="flex items-center space-x-3 p-3 border rounded-lg hover:bg-gray-50">
                <FileText className="w-5 h-5 text-blue-600" />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{document.name}</p>
                  <p className="text-sm text-gray-500">{document.type}</p>
                </div>
                <button className="text-blue-600 hover:text-blue-800">
                  <Download className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeView) {
      case 'dashboard':
        return renderDashboard();
      case 'contracts':
        return renderContracts();
      case 'contract-detail':
        return renderContractDetail();
      default:
        return renderDashboard();
    }
  };

  return (
    <div className="p-6">
      {renderWorkflowBanner()}
      
      {/* Navigation Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveView('dashboard')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'dashboard'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            儀表板
          </button>
          <button
            onClick={() => setActiveView('contracts')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeView === 'contracts'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            合約清單
          </button>
        </nav>
      </div>

      {renderContent()}
    </div>
  );
};

export default ContractManagement; 